// ────────────────────────────────────────────────────────────────────────────
// The workflow STATE MACHINE: which states each track has, their labels, the
// moves between them, and who performs each move. Product decisions, mapping to
// the `OrderStatus` / `LoadStatus` enums — which is why they are code and not
// stored per organisation.
//
// The condition and behaviour VALUES below are seeds only, and are NOT the
// authority at runtime: the backend ships the default policy and returns it from
// `GET /workflowpolicies`, and `applyPolicy` lays that over this structure
// before anything is rendered. Change a default in
// `WorkflowPolicyDefaults` (BE), not here — a change here reaches nobody.
// ────────────────────────────────────────────────────────────────────────────
// Prototype data for the workflow settings screens.
//
// FE-only for now, deliberately: the point of this pass is to choose a layout
// before any of it is stored. The shape below is the one intended for the real
// thing, so swapping mock data for an API response should not change the
// components — same reasoning as the Documents builder, which started here too.
//
// Severity mirrors the invoice-approval policy already in the backend
// (`ApprovalSeverity`: ignore / warn / stop) rather than inventing a second
// vocabulary — an operator meets the concept once and it means one thing.
// ────────────────────────────────────────────────────────────────────────────

/** What an unmet requirement does to the transition. */
export type Severity = 'ignore' | 'warn' | 'stop';

export const SEVERITY_LABEL: Record<Severity, string> = {
  ignore: 'Off',
  warn: 'Warn',
  stop: 'Block',
};

/**
 * Which way a check points.
 *
 * `require` — the fact must HOLD before the move is allowed. "You may not open
 * an order without a customer reference."
 *
 * `prevent` — the fact must NOT hold; its presence stops the move. This is the
 * only frame that makes sense for cancellation: nothing has to be TRUE to
 * abandon an order, but plenty of things should stop you. Asking "what must be
 * true before this can be cancelled?" produces rules nobody wants.
 *
 * A check is not automatically usable in both directions — see `polarities` on
 * the catalogue entry, and the warning on `customerPaid`.
 */
export type Polarity = 'require' | 'prevent';

/**
 * Deliberately NOT "Block": severity already uses that word for `stop`, and two
 * different Blocks on one row is how an operator learns to distrust the screen.
 */
export const POLARITY_LABEL: Record<Polarity, string> = {
  require: 'Required',
  prevent: 'Prevents',
};

/**
 * What a track's states belong to. Conditions are filtered by this: a trip has
 * no customer reference and an order has no driver, so offering either would
 * invite a rule that can never be satisfied.
 */
export type Subject = 'order' | 'trip' | 'leg';

/**
 * State keys are TRACK-LOCAL strings, not a shared union.
 *
 * They were a shared union at first and it was wrong: Movement's states had to
 * borrow Order's keys and be relabelled, so "assigned" arrived wearing the name
 * "inProgress". Each track maps to its own backend enum, so each owns its own
 * keys.
 */
export type StateKey = string;

/**
 * The kinds of requirement a transition can carry. Each is a named check with
 * its own parameters — a list rather than a fixed set of switches, so a new
 * check is data plus one evaluator branch, never a schema change.
 */
export type RequirementKind =
  | 'customerReference'
  | 'minTasks'
  | 'hasCollection'
  | 'hasDelivery'
  | 'charges'
  | 'plannedDates'
  | 'cargo'
  | 'hasDriver'
  | 'hasVehicle'
  | 'hasLines'
  | 'allLegsCompleted'
  | 'allTasksFinished'
  | 'podSigned'
  | 'customerPaid'
  | 'invoiced'
  | 'paymentReceived'
  | 'supplierPaid'
  | 'workStarted'
  | 'fromState';

export interface RequirementMeta {
  kind: RequirementKind;
  label: string;
  /** Shown under the row — says what "unmet" actually means. */
  detail: string;
  /** Rendered where the check takes a parameter. */
  param?: string;
  /** Which subjects this check can be applied to. */
  appliesTo: Subject[];
  /**
   * Directions this check may be used in. Defaults to `['require']` — a check
   * has to opt IN to being invertible, because inverting a check that
   * auto-satisfies when it does not apply produces a rule that fires on
   * records it was never about (`customerPaid` is the worked example).
   */
  polarities?: Polarity[];
  /**
   * How the blocked case reads, as a positive statement of the fact. The
   * `require` direction reports what is MISSING ("No charge lines"); the
   * `prevent` direction has to report what is PRESENT ("Has charge lines"),
   * and negating the first mechanically gives nonsense.
   */
  preventReason?: string;
  /**
   * True where satisfying the check depends on ANOTHER track. These are the
   * rules that make a two-level workflow work, and the ones most likely to be
   * misread as automation — flagged so the UI can say so.
   */
  crossTrack?: boolean;
}

export const REQUIREMENT_CATALOGUE: RequirementMeta[] = [
  {
    kind: 'customerReference',
    label: 'Customer reference',
    detail: 'The chosen reference field must be filled in.',
    // 1-based reference slot; the screen shows the organisation's label for it.
    param: '1',
    appliesTo: ['order'],
  },
  {
    kind: 'minTasks',
    label: 'Minimum tasks',
    detail: 'The order must carry at least this many tasks.',
    param: '1',
    appliesTo: ['order'],
  },
  // No parameter. WHICH task types count as collecting or delivering follows
  // from the cargo type — the catalogue of legal task types per cargo already
  // says so, and `cargoHandlingPhaseFor` already sorts them into load and
  // delivery. Offering the list as chips invited an operator to configure
  // something the evaluator does not read.
  {
    kind: 'hasCollection',
    label: 'Has a collection',
    detail: 'At least one collecting task, per the cargo type.',
    appliesTo: ['order'],
  },
  {
    kind: 'hasDelivery',
    label: 'Has a delivery',
    detail: 'At least one delivering task, per the cargo type.',
    appliesTo: ['order'],
  },
  {
    kind: 'charges',
    label: 'Has charges',
    detail: 'At least one charge line with a non-zero net.',
    preventReason: 'Has charge lines',
    appliesTo: ['order'],
    polarities: ['require', 'prevent'],
  },
  {
    kind: 'plannedDates',
    label: 'Planned dates set',
    detail: 'Every task or leg carries a planned date.',
    appliesTo: ['order', 'trip', 'leg'],
  },
  {
    kind: 'cargo',
    label: 'Cargo entered',
    detail: 'At least one cargo item on a collecting task.',
    appliesTo: ['order', 'leg'],
  },
  {
    kind: 'hasLines',
    label: 'Has cargo assigned',
    detail: 'At least one cargo line is on the trip.',
    param: '1',
    appliesTo: ['trip'],
  },
  {
    kind: 'hasDriver',
    label: 'Driver assigned',
    detail: 'A driver occupies the Driver slot on the trip.',
    appliesTo: ['trip'],
  },
  {
    kind: 'hasVehicle',
    label: 'Vehicle assigned',
    detail: 'A vehicle occupies the Vehicle slot on the trip.',
    appliesTo: ['trip'],
  },
  {
    kind: 'allLegsCompleted',
    label: 'All legs completed',
    detail: 'Every leg on the trip has reached Completed.',
    appliesTo: ['trip'],
    crossTrack: true,
  },
  {
    // The order's own legs, so not cross-track: an order holds its tasks.
    kind: 'allTasksFinished',
    label: 'No open tasks',
    detail: 'Every task is Completed or Cancelled — none still pending or in progress.',
    appliesTo: ['order'],
  },
  {
    kind: 'podSigned',
    label: 'POD received',
    // On an order, a POD document accounts for a leg exactly as a signature
    // does — `orders/lib/pod.ts`, mirrored by the invoicing POD check.
    detail: 'Every delivery leg has a signed POD or a proof-of-delivery document attached.',
    appliesTo: ['order', 'trip', 'leg'],
  },
  {
    kind: 'customerPaid',
    label: 'Customer paid',
    detail:
      "For cash (prepaid) customers, the order's invoice must be settled before the work may start. Not applied to credit, self-billed or internal terms.",
    appliesTo: ['order', 'trip'],
    crossTrack: true,
    // REQUIRE ONLY, and not an oversight. This check answers "is the prepayment
    // rule satisfied?", and it answers YES for a credit customer — the rule
    // does not apply to them. Inverted, that "yes" would read as "the customer
    // has paid" and would block every credit order from being cancelled.
    // `paymentReceived` below is the plain fact, and that is what inverts.
    polarities: ['require'],
  },
  {
    kind: 'invoiced',
    label: 'Already invoiced',
    detail: 'A customer invoice has been raised against this order.',
    preventReason: 'Already invoiced — credit it instead',
    appliesTo: ['order'],
    polarities: ['prevent'],
  },
  {
    kind: 'paymentReceived',
    label: 'Payment received',
    detail: 'Money has been received against this order, whatever the terms.',
    preventReason: 'Payment received — refund it first',
    appliesTo: ['order', 'trip'],
    polarities: ['prevent'],
    crossTrack: true,
  },
  {
    kind: 'supplierPaid',
    label: 'Supplier paid',
    detail: 'A cost on this job has been settled with a subcontractor, ferry operator or other supplier.',
    // The cost side, and the one that actually loses money: cancelling an order
    // does not get a ferry booking back.
    preventReason: 'A supplier has already been paid',
    appliesTo: ['order', 'trip'],
    polarities: ['prevent'],
    crossTrack: true,
  },
  {
    // The one check that reads the MOVE rather than the record: it asks which
    // state the record is sitting in. That is what makes a state-specific rule
    // expressible inside a single track-wide policy — the alternative was a
    // separate rule set per from-state, which is what this screen just stopped
    // doing.
    kind: 'fromState',
    label: 'Reached a given state',
    // A THRESHOLD, not a set. States run in order, so "not from In progress"
    // necessarily means "not from Completed" either — offering them as
    // independent tick boxes would let an operator save a rule that says a job
    // may not be cancelled once it is moving but may be once it is finished.
    detail: 'Blocks the move once the record has reached this state, and from every state after it.',
    preventReason: 'Too far along to cancel',
    appliesTo: ['order', 'trip', 'leg'],
    polarities: ['prevent'],
  },
  {
    kind: 'workStarted',
    label: 'Work has started',
    detail: 'At least one leg has been started or completed.',
    preventReason: 'Work has already started',
    appliesTo: ['trip'],
    polarities: ['prevent'],
  },
];

export interface Requirement {
  kind: RequirementKind;
  severity: Severity;
  /** Which way the check points. Absent means `require`. */
  polarity?: Polarity;
  /** Overrides the catalogue default where the check is parameterised. */
  param?: string;
}

/** A requirement's direction, defaulted. */
export function polarityOf(r: Requirement): Polarity {
  return r.polarity ?? 'require';
}

/**
 * Identity of a row within a transition. Kind alone is not enough: a check
 * usable in both directions could legitimately appear twice.
 */
export function requirementId(r: Requirement): string {
  return `${r.kind}:${polarityOf(r)}`;
}

export interface WorkflowTransition {
  from: StateKey;
  to: StateKey;
  requirements: Requirement[];
  /**
   * Who moves it. Some states are reached by the driver's app rather than by an
   * operator at a desk, which changes who the conditions are protecting.
   */
  setBy?: 'operator' | 'driverApp' | 'system';
}

/**
 * Behaviour of a state, as distinct from the requirements to ENTER it.
 * "A draft only appears on the Orders screen" is a property of the state, not a
 * rule about reaching it, so it lives here rather than in the requirement list.
 */
export interface StateBehaviour {
  /** Visible in the planning cockpit / manifest queues. */
  plannable: boolean;
  /** Still editable by an operator. */
  editable: boolean;
  /**
   * A CREDIT customer's order in this state shows on the invoicing screen.
   *
   * <p>
   * Credit only, and that is the whole reason this can be a plain switch. A
   * cash order has to be settled while it is Open — that is the `customerPaid`
   * requirement on `open → inProgress` — so cash is forced to be invoiceable
   * from Open and there is nothing to configure. Self-billed and internal are
   * out of scope for now: the customer raises their own document, or the work
   * is in-house.
   * </p>
   *
   * <p>
   * Order tracks only. A trip and a leg are never invoiced, so the flag is
   * absent there rather than present and meaningless.
   * </p>
   */
  invoiceable?: boolean;
}

export interface WorkflowState {
  key: StateKey;
  label: string;
  /** Terminal states end the flow — nothing follows them. */
  terminal: boolean;
  /**
   * Reaching this state ABANDONS the record rather than completing it. Declared
   * on the state instead of inferred from a `'cancelled'` key, because state
   * keys are track-local strings and a track may one day call it something else
   * ("Aborted", "Void").
   */
  abandons?: boolean;
  behaviour: StateBehaviour;
}

export interface Workflow {
  key: string;
  label: string;
  description: string;
  subject: Subject;
  /** Set where the states exist but nothing yet drives them. */
  note?: string;
  states: WorkflowState[];
  transitions: WorkflowTransition[];
  /**
   * ONE cancellation policy for the whole track, rather than a rule set per
   * from-state.
   *
   * <p>
   * Per-state cancellation rules were the first shape and they were wrong: the
   * same two blockers had to be repeated on every from-state, and forgetting
   * one made cancellation behave differently depending on where the record
   * happened to sit. Because blockers are FACTS, one shared set is also safe —
   * a fact that cannot hold for a state simply never fires, so a draft (never
   * invoiced, never paid) stays unconditionally cancellable without being
   * special-cased.
   * </p>
   */
  cancelRequirements: Requirement[];
}

/**
 * A heading in the settings screen, holding one or more state machines.
 *
 * The grouping is not cosmetic — **Movement** genuinely contains TWO machines,
 * and flattening them is how workflow features go wrong: a load carries a
 * status, each leg carries a state, and the interesting rules live BETWEEN them
 * (does completing every leg complete the trip?). One shared state list would
 * force the wrong states onto half of it.
 *
 * Payment was intentionally NOT modelled as a workflow: invoicing rides on the
 * customer's payment terms + the existing invoice-approval policy, so a separate
 * state machine here would only duplicate it. Payment readiness is a
 * payment-terms rule (`isInvoiceable`), and the one operational gate it needs —
 * a cash order paid before work starts — is the `customerPaid` requirement.
 */
export interface WorkflowGroup {
  key: 'order' | 'movement';
  label: string;
  description: string;
  tracks: Workflow[];
}

const ORDER: Workflow = {
  key: 'order',
  label: 'Order',
  subject: 'order',
  description:
    'How an order moves from being typed in to being finished, and what has to be true before it may move.',
  states: [
    {
      key: 'draft',
      label: 'Draft',
      terminal: false,
      behaviour: { plannable: false, editable: true, invoiceable: false },
    },
    {
      key: 'open',
      label: 'Open',
      terminal: false,
      // Invoiceable from here by default: raising a credit customer's invoice
      // at collection time is ordinary practice, and the switch cascades
      // forward so In progress and Completed follow.
      behaviour: { plannable: true, editable: true, invoiceable: true },
    },
    {
      key: 'inProgress',
      label: 'In progress',
      terminal: false,
      behaviour: { plannable: true, editable: true, invoiceable: true },
    },
    {
      key: 'completed',
      label: 'Completed',
      terminal: true,
      behaviour: { plannable: false, editable: false, invoiceable: true },
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      terminal: true,
      abandons: true,
      behaviour: { plannable: false, editable: false, invoiceable: false },
    },
  ],
  transitions: [
    {
      from: 'draft',
      to: 'open',
      setBy: 'operator',
      requirements: [
        { kind: 'customerReference', severity: 'stop', param: '1' },
        { kind: 'minTasks', severity: 'stop', param: '1' },
        { kind: 'hasCollection', severity: 'stop' },
        { kind: 'hasDelivery', severity: 'warn' },
      ],
    },
    {
      from: 'open',
      to: 'inProgress',
      setBy: 'operator',
      requirements: [
        // Cash (prepaid) orders can't start work until the invoice is paid;
        // the check no-ops for credit / self-billed / internal terms.
        { kind: 'customerPaid', severity: 'stop' },
        { kind: 'plannedDates', severity: 'warn' },
      ],
    },
    {
      from: 'inProgress',
      to: 'completed',
      setBy: 'operator',
      requirements: [
        { kind: 'charges', severity: 'warn' },
        { kind: 'cargo', severity: 'ignore' },
        { kind: 'allTasksFinished', severity: 'stop' },
        { kind: 'podSigned', severity: 'stop' },
      ],
    },
    // The cancellation policy is track-level (`cancelRequirements` below), so
    // these carry nothing of their own.
    { from: 'draft', to: 'cancelled', setBy: 'operator', requirements: [] },
    { from: 'open', to: 'cancelled', setBy: 'operator', requirements: [] },
    { from: 'inProgress', to: 'cancelled', setBy: 'operator', requirements: [] },
  ],
  // THE SHIPPED DEFAULT — an order may be cancelled right up until money has
  // moved in either direction: a customer invoice raised, or a supplier paid
  // for work on this job. Everything before that is just a plan being dropped.
  cancelRequirements: [
    { kind: 'invoiced', polarity: 'prevent', severity: 'stop' },
    { kind: 'supplierPaid', polarity: 'prevent', severity: 'stop' },
    // The customer having paid is the third money-has-moved case; a block by
    // default because a refund is a decision, not a side effect of cancelling.
    { kind: 'paymentReceived', polarity: 'prevent', severity: 'stop' },
    // Work under way. A WARNING rather than a block on purpose: abandoning a
    // job mid-route is sometimes exactly what happened, and an operator
    // recording reality should not be fought. Promote it to Block if the
    // business genuinely never allows it.
    { kind: 'fromState', polarity: 'prevent', severity: 'warn', param: 'inProgress' },
  ],
};

/**
 * `LoadStatus` — the trip.
 *
 * Maps onto the existing enum by RENAME only, so no migration and no data
 * change: Draft(0) becomes Open, Dispatched(2) becomes Assigned, Planned and
 * Completed keep their names, Cancelled(4) stays as the escape.
 *
 * Note there is no in-transit state here on purpose — progress along the road
 * belongs to the legs, and the trip only tracks planning and assignment.
 */
const MOVEMENT_TRIP: Workflow = {
  key: 'movement.trip',
  label: 'Trip',
  subject: 'trip',
  description: 'A load: opened, planned, assigned to a driver, completed.',
  note: 'Renames the existing LoadStatus values — Draft becomes Open, Dispatched becomes Assigned. Nothing advances them automatically today.',
  states: [
    {
      key: 'open',
      label: 'Open',
      terminal: false,
      behaviour: { plannable: true, editable: true },
    },
    {
      key: 'planned',
      label: 'Planned',
      terminal: false,
      behaviour: { plannable: true, editable: true },
    },
    {
      key: 'assigned',
      label: 'Assigned',
      terminal: false,
      behaviour: { plannable: true, editable: true },
    },
    {
      key: 'completed',
      label: 'Completed',
      terminal: true,
      behaviour: { plannable: false, editable: false },
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      terminal: true,
      abandons: true,
      behaviour: { plannable: false, editable: false },
    },
  ],
  transitions: [
    {
      from: 'open',
      to: 'planned',
      setBy: 'operator',
      requirements: [
        { kind: 'hasLines', severity: 'stop', param: '1' },
        { kind: 'plannedDates', severity: 'warn' },
      ],
    },
    {
      from: 'planned',
      to: 'assigned',
      // The driver's app sets this, not an operator at a desk — so the
      // conditions here protect the app from accepting an unworkable trip,
      // including a cash job that hasn't been paid for yet.
      setBy: 'driverApp',
      requirements: [
        { kind: 'customerPaid', severity: 'stop' },
        { kind: 'hasDriver', severity: 'stop' },
        { kind: 'hasVehicle', severity: 'warn' },
      ],
    },
    {
      from: 'assigned',
      to: 'completed',
      setBy: 'operator',
      requirements: [
        { kind: 'allLegsCompleted', severity: 'stop' },
        { kind: 'podSigned', severity: 'warn' },
      ],
    },
    { from: 'open', to: 'cancelled', setBy: 'operator', requirements: [] },
    { from: 'planned', to: 'cancelled', setBy: 'operator', requirements: [] },
    { from: 'assigned', to: 'cancelled', setBy: 'operator', requirements: [] },
  ],
  // A trip is a plan until a supplier has been paid for it or a driver has
  // actually started. `workStarted` cannot hold before Assigned, so one shared
  // set needs no per-state exception.
  cancelRequirements: [
    { kind: 'supplierPaid', polarity: 'prevent', severity: 'stop' },
    { kind: 'workStarted', polarity: 'prevent', severity: 'warn' },
  ],
};

/** `TaskState` — one leg. */
const MOVEMENT_LEG: Workflow = {
  key: 'movement.leg',
  label: 'Leg',
  subject: 'leg',
  description: 'A single stop — collect or deliver.',
  note: 'The rules that matter sit between the two tracks: "All legs completed" on the trip is what links them.',
  states: [
    {
      key: 'pending',
      label: 'Pending',
      terminal: false,
      behaviour: { plannable: true, editable: true },
    },
    {
      key: 'inProgress',
      label: 'In progress',
      terminal: false,
      behaviour: { plannable: true, editable: true },
    },
    {
      key: 'completed',
      label: 'Completed',
      terminal: true,
      behaviour: { plannable: false, editable: false },
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      terminal: true,
      abandons: true,
      behaviour: { plannable: false, editable: false },
    },
  ],
  transitions: [
    { from: 'pending', to: 'inProgress', setBy: 'driverApp', requirements: [] },
    {
      from: 'inProgress',
      to: 'completed',
      setBy: 'driverApp',
      requirements: [
        { kind: 'cargo', severity: 'warn' },
        { kind: 'podSigned', severity: 'warn' },
      ],
    },
    { from: 'pending', to: 'cancelled', setBy: 'operator', requirements: [] },
    { from: 'inProgress', to: 'cancelled', setBy: 'operator', requirements: [] },
  ],
  // A leg is the smallest unit and carries no money of its own — dropping one
  // is a planning decision. Left empty rather than invented: a default nobody
  // asked for is a default nobody understands.
  cancelRequirements: [],
};

export const WORKFLOW_GROUPS: WorkflowGroup[] = [
  {
    key: 'order',
    label: 'Order',
    description: 'The commercial life of an order.',
    tracks: [ORDER],
  },
  {
    key: 'movement',
    label: 'Movement',
    description: 'The physical execution — the trip, and each leg of it.',
    tracks: [MOVEMENT_TRIP, MOVEMENT_LEG],
  },
];

export function stateLabel(workflow: Workflow, key: StateKey): string {
  return workflow.states.find((s) => s.key === key)?.label ?? key;
}

/**
 * True where the move ABANDONS the record rather than advancing it. Terminal
 * states reached by cancelling are the abandoning ones, and they read the other
 * way round: nothing must be true to cancel, but things can stop you.
 */
export function abandonsRecord(workflow: Workflow, transition: WorkflowTransition): boolean {
  const target = workflow.states.find((st) => st.key === transition.to);
  return target?.abandons === true;
}

/**
 * Heading for a transition's condition list. Shared by all three candidate
 * layouts so the wording cannot drift between them — and it must not say
 * "before X can become Cancelled", which invites the operator to list what has
 * to be TRUE in order to abandon an order. Nothing does.
 */
export function transitionTitle(workflow: Workflow, transition: WorkflowTransition): string {
  const from = stateLabel(workflow, transition.from);
  if (abandonsRecord(workflow, transition)) {
    return `Cancelling from ${from}`;
  }
  return `Before ${from} can become ${stateLabel(workflow, transition.to)}`;
}

/** The track's states in order, excluding the abandoning one. */
export function chainStates(workflow: Workflow): WorkflowState[] {
  return workflow.states.filter((s) => s.abandons !== true);
}

/**
 * A threshold state and everything after it. `fromState` is STORED as one key
 * and EVALUATED as the set it implies — expanding here keeps the stored rule
 * honest about what the operator picked while letting the evaluator stay a
 * plain membership test that needs no knowledge of the track's order.
 */
export function statesFromOnwards(workflow: Workflow, key: StateKey): StateKey[] {
  const chain = chainStates(workflow).map((s) => s.key);
  const at = chain.indexOf(key);
  return at < 0 ? [] : chain.slice(at);
}

/**
 * The rules that actually apply to a move: the track's cancellation policy for
 * an abandoning transition, the transition's own list otherwise, with every
 * `fromState` threshold expanded to the states it covers.
 */
export function effectiveRequirements(
  workflow: Workflow,
  transition: WorkflowTransition,
): Requirement[] {
  const base = abandonsRecord(workflow, transition)
    ? workflow.cancelRequirements
    : transition.requirements;

  if (!base.some((r) => r.kind === 'fromState')) return base;
  return base.map((r) =>
    r.kind === 'fromState'
      ? { ...r, param: statesFromOnwards(workflow, (r.param ?? '').trim()).join(', ') }
      : r,
  );
}

/** The transition as the evaluator should see it — see `effectiveRequirements`. */
export function effectiveTransition(
  workflow: Workflow,
  transition: WorkflowTransition,
): WorkflowTransition {
  const requirements = effectiveRequirements(workflow, transition);
  return requirements === transition.requirements ? transition : { ...transition, requirements };
}

/** The stored threshold on a requirement list, or null when none is set. */
export function fromStateThreshold(requirements: Requirement[]): StateKey | null {
  const r = requirements.find((x) => x.kind === 'fromState');
  const key = (r?.param ?? '').trim();
  return key === '' ? null : key;
}

export function requirementMeta(kind: RequirementKind): RequirementMeta {
  return (
    REQUIREMENT_CATALOGUE.find((r) => r.kind === kind) ?? {
      kind,
      label: kind,
      detail: '',
      appliesTo: [],
    }
  );
}

/** The checks that can sensibly be applied to a subject, in one direction. */
export function catalogueFor(subject: Subject, polarity: Polarity = 'require'): RequirementMeta[] {
  return REQUIREMENT_CATALOGUE.filter(
    (r) => r.appliesTo.includes(subject) && (r.polarities ?? ['require']).includes(polarity),
  );
}

/** Requirements that actually gate the move — `ignore` is switched off. */
export function activeRequirements(t: WorkflowTransition): Requirement[] {
  return t.requirements.filter((r) => r.severity !== 'ignore');
}

export function blockingCount(t: WorkflowTransition): number {
  return t.requirements.filter((r) => r.severity === 'stop').length;
}

/**
 * Moves a state can make next, EXCLUDING cancellation.
 *
 * Cancellation is deliberately absent: it is available from nearly every state,
 * it is governed by one track-wide policy rather than a per-state rule set, and
 * it has its own standing section on the screen. Listing it per state would
 * offer an editor that edits the same thing however you reach it.
 */
export function outgoing(workflow: Workflow, from: StateKey): WorkflowTransition[] {
  return workflow.transitions.filter((t) => t.from === from && !abandonsRecord(workflow, t));
}

/** Every move, cancellation included — for a whole-machine view. */
export function allOutgoing(workflow: Workflow, from: StateKey): WorkflowTransition[] {
  return workflow.transitions.filter((t) => t.from === from);
}

export const SET_BY_LABEL: Record<NonNullable<WorkflowTransition['setBy']>, string> = {
  operator: 'Set by an operator',
  driverApp: 'Set by the driver app',
  system: 'Set automatically',
};

// ── Billability (a payment-terms rule, NOT a state property) ─────────────────

/** How the customer is billed — drives when (and whether) an order is invoiceable. */
export type PaymentTerms = 'cash' | 'credit' | 'selfBilled' | 'internal';

/** In the order an operator thinks about them, with the labels they use. */
export const PAYMENT_TERMS: { key: PaymentTerms; label: string }[] = [
  { key: 'cash', label: 'Cash' },
  { key: 'credit', label: 'Credit' },
  { key: 'selfBilled', label: 'Self-billed' },
  { key: 'internal', label: 'Internal' },
];

/**
 * Whether an order may be BILLED — that is, whether it shows up on the
 * invoicing screen at all.
 *
 * <p>
 * Deliberately NOT split by cash vs credit. Both may be invoiced from `open`;
 * an operator raising a credit customer's invoice at collection time is
 * ordinary practice, and hiding the order until Completed would just stop them
 * doing their job. What differs between the two is WHEN THE MONEY IS DUE, and
 * that is a separate rule already carried by the `customerPaid` requirement on
 * `open → inProgress`: a cash order must be settled before the work starts, a
 * credit order need not be. Conflating the two put "pay before we start" into
 * the answer to "can this be invoiced", which is a different question.
 * </p>
 *
 * <p>
 * Self-billed and internal are never invoiced by us whatever the state — the
 * customer raises their own document, or the work is inside the company. So
 * this is still (terms × state) and still not a per-state flag, which is why it
 * is not in `StateBehaviour`.
 * </p>
 */
export function isInvoiceable(
  workflow: Workflow,
  terms: PaymentTerms,
  orderState: StateKey,
): boolean {
  // Out of scope for now: the customer raises their own document, or the work
  // is in-house.
  if (terms === 'selfBilled' || terms === 'internal') return false;

  // Cash is not configurable. The prepay rule already says the invoice must be
  // settled while the order is Open, so it has to be raisable from Open — a
  // switch offering anything else would contradict a Block on another screen.
  if (terms === 'cash') return orderState !== 'draft' && orderState !== 'cancelled';

  // Credit: whatever the state's own switch says.
  return workflow.states.find((s) => s.key === orderState)?.behaviour.invoiceable === true;
}

/**
 * Move the point at which invoicing opens up, and carry the consequence through
 * the chain.
 *
 * <p>
 * The setting is really a THRESHOLD — invoicing starts at some state and stays
 * open from there — so switching one state on opens every later state, and
 * switching one off pushes the threshold later rather than punching a hole. An
 * order billable once work starts is still billable when the work is done (it
 * may not be fully invoiced yet), so a gap in the middle describes nothing
 * real, and enforcing that here makes it unsaveable.
 * </p>
 *
 * <p>
 * The FINAL state is a floor and cannot be switched off: a finished order that
 * can never be invoiced is not a configuration anyone wants, it is a job that
 * can never be billed. So the switch controls how EARLY invoicing opens, never
 * whether it opens at all.
 * </p>
 */
export function withInvoiceableFrom(
  workflow: Workflow,
  key: StateKey,
  value: boolean,
): WorkflowState[] {
  const chain = chainStates(workflow).map((st) => st.key);
  const at = chain.indexOf(key);
  if (at < 0) return workflow.states;
  const floor = chain.length - 1;

  // Closing the floor is a no-op, not "close everything earlier". The card
  // disables that switch so it cannot be asked for, but a helper that quietly
  // rewrote three other states when handed an impossible request would be a
  // trap for the next caller.
  if (at >= floor && !value) return workflow.states;

  return workflow.states.map((st) => {
    const i = chain.indexOf(st.key);
    // Off-chain (Cancelled) is never invoiceable.
    if (i < 0) {
      return st.behaviour.invoiceable === false
        ? st
        : { ...st, behaviour: { ...st.behaviour, invoiceable: false } };
    }
    const next =
      i >= floor
        ? true
        : value
          ? i >= at
          : i > at
            ? st.behaviour.invoiceable === true
            : false;
    return next === st.behaviour.invoiceable
      ? st
      : { ...st, behaviour: { ...st.behaviour, invoiceable: next } };
  });
}

/** True where this state is the floor — invoicing can never be closed here. */
export function isInvoiceableFloor(workflow: Workflow, key: StateKey): boolean {
  const chain = chainStates(workflow);
  return chain.length > 0 && chain[chain.length - 1]!.key === key;
}

/** The earliest state a credit order becomes invoiceable, or null. */
export function invoiceableFrom(workflow: Workflow): WorkflowState | null {
  return chainStates(workflow).find((st) => st.behaviour.invoiceable === true) ?? null;
}
